import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'terms' | 'privacy';
}
export function LegalModal({
  isOpen,
  onClose,
  initialTab = 'terms'
}: LegalModalProps) {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>(initialTab);
  // Sync tab when modal opens with a specific tab
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);
  return <AnimatePresence data-id="element-134">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-135">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-dark/40 backdrop-blur-sm" onClick={onClose} data-id="element-136" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-gold/10" data-id="element-137">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-138">
              <div className="flex gap-1 bg-gray-100 rounded-full p-1" data-id="element-139">
                <button onClick={() => setActiveTab('terms')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'terms' ? 'bg-white text-dark shadow-sm' : 'text-gray-500 hover:text-dark'}`} data-id="element-140">
                  Términos y Condiciones
                </button>
                <button onClick={() => setActiveTab('privacy')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'privacy' ? 'bg-white text-dark shadow-sm' : 'text-gray-500 hover:text-dark'}`} data-id="element-141">
                  Política de Privacidad
                </button>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-dark hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-142">
                <X className="w-5 h-5" data-id="element-143" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-144">
              {activeTab === 'terms' ? <TermsContent data-id="element-145" /> : <PrivacyContent data-id="element-146" />}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 text-center" data-id="element-147">
              <button onClick={onClose} className="px-6 py-2 bg-gold text-white rounded-full text-sm font-bold hover:bg-gold/90 transition-colors" data-id="element-148">
                Entendido
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function TermsContent() {
  return <div className="prose prose-sm max-w-none text-gray-700 space-y-4" data-id="element-149">
      <h2 className="text-xl font-serif font-bold text-dark" data-id="element-150">
        Términos y Condiciones
      </h2>
      <p className="text-xs text-gray-400" data-id="element-151">
        Última actualización: Febrero 2026
      </p>

      <h3 className="font-bold text-dark text-base mt-6" data-id="element-152">
        1. Aceptación de los Términos
      </h3>
      <p data-id="element-153">
        Al acceder y realizar una compra en este sitio web de Ansiedad Exprés,
        usted acepta estar sujeto a estos Términos y Condiciones de uso. Si no
        está de acuerdo con alguno de estos términos, le rogamos no utilice
        nuestros servicios ni realice compras.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-154">
        2. Descripción del Producto
      </h3>
      <p data-id="element-155">
        Ansiedad Exprés es un producto digital (ebook) que contiene técnicas y
        protocolos de manejo de ansiedad. Este producto es de carácter
        informativo y educativo.{' '}
        <strong data-id="element-156">
          No constituye asesoría médica, psicológica ni psiquiátrica
          profesional.
        </strong>
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-157">
        3. Naturaleza del Producto Digital
      </h3>
      <ul className="list-disc pl-5 space-y-1" data-id="element-158">
        <li data-id="element-159">
          El producto se entrega en formato digital (PDF/ebook) mediante
          descarga inmediata tras la confirmación del pago.
        </li>
        <li data-id="element-160">
          Por la naturaleza digital e inmediata de la entrega,{' '}
          <strong data-id="element-161">no se aceptan devoluciones ni reembolsos</strong> una vez
          completada la descarga.
        </li>
        <li data-id="element-162">El acceso al producto es personal e intransferible.</li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-163">
        4. Precio y Forma de Pago
      </h3>
      <ul className="list-disc pl-5 space-y-1" data-id="element-164">
        <li data-id="element-165">
          El precio del producto es de $111.99 MXN (pesos mexicanos), pago
          único.
        </li>
        <li data-id="element-166">
          Los pagos se procesan a través de plataformas de pago seguras de
          terceros.
        </li>
        <li data-id="element-167">El precio puede estar sujeto a cambios sin previo aviso.</li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-168">
        5. Propiedad Intelectual
      </h3>
      <p data-id="element-169">
        Todo el contenido del ebook, incluyendo textos, gráficos, audios y
        diseños, es propiedad exclusiva de Ansiedad Exprés y está protegido por
        las leyes de derechos de autor. Queda estrictamente prohibido:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-170">
        <li data-id="element-171">
          Reproducir, distribuir o compartir el contenido total o parcialmente.
        </li>
        <li data-id="element-172">Modificar, adaptar o crear obras derivadas.</li>
        <li data-id="element-173">
          Usar el contenido con fines comerciales sin autorización expresa.
        </li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-174">
        6. Limitación de Responsabilidad
      </h3>
      <p data-id="element-175">Ansiedad Exprés y sus creadores:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-176">
        <li data-id="element-177">
          <strong data-id="element-178">No garantizan resultados específicos.</strong> Los resultados
          pueden variar según cada persona.
        </li>
        <li data-id="element-179">No se hacen responsables por el uso inadecuado del contenido.</li>
        <li data-id="element-180">
          No sustituyen la atención profesional de salud mental. Si experimenta
          síntomas severos o crónicos de ansiedad, consulte a un profesional de
          salud calificado.
        </li>
        <li data-id="element-181">
          No serán responsables por daños directos, indirectos, incidentales o
          consecuentes derivados del uso del producto.
        </li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-182">7. Disclaimer de Salud</h3>
      <p data-id="element-183">
        El contenido de este producto es exclusivamente informativo y educativo.{' '}
        <strong data-id="element-184">
          No pretende diagnosticar, tratar, curar ni prevenir ninguna enfermedad
          o condición médica.
        </strong>{' '}
        Siempre consulte con un profesional de salud mental antes de implementar
        cualquier técnica si tiene una condición preexistente.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-185">8. Modificaciones</h3>
      <p data-id="element-186">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Las modificaciones entrarán en vigor desde su publicación en
        este sitio web.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-187">
        9. Legislación Aplicable
      </h3>
      <p data-id="element-188">
        Estos términos se rigen por las leyes aplicables de los Estados Unidos
        Mexicanos. Cualquier controversia será sometida a la jurisdicción de los
        tribunales competentes.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-189">10. Contacto</h3>
      <p data-id="element-190">
        Para cualquier consulta relacionada con estos términos, puede
        contactarnos a través de los canales de comunicación disponibles en este
        sitio web.
      </p>
    </div>;
}
function PrivacyContent() {
  return <div className="prose prose-sm max-w-none text-gray-700 space-y-4" data-id="element-191">
      <h2 className="text-xl font-serif font-bold text-dark" data-id="element-192">
        Política de Privacidad
      </h2>
      <p className="text-xs text-gray-400" data-id="element-193">
        Última actualización: Febrero 2026
      </p>

      <h3 className="font-bold text-dark text-base mt-6" data-id="element-194">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-195">
        Al realizar una compra o interactuar con nuestro sitio, podemos
        recopilar:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-196">
        <li data-id="element-197">
          <strong data-id="element-198">Datos personales:</strong> nombre, dirección de correo
          electrónico.
        </li>
        <li data-id="element-199">
          <strong data-id="element-200">Datos de pago:</strong> procesados de forma segura por
          plataformas de pago de terceros. No almacenamos datos de tarjetas de
          crédito/débito.
        </li>
        <li data-id="element-201">
          <strong data-id="element-202">Datos de navegación:</strong> dirección IP, tipo de navegador,
          páginas visitadas, tiempo de permanencia (mediante cookies y
          herramientas de análisis).
        </li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-203">
        2. Uso de la Información
      </h3>
      <p data-id="element-204">Utilizamos su información para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-205">
        <li data-id="element-206">Procesar y entregar su compra.</li>
        <li data-id="element-207">Enviar el producto digital adquirido.</li>
        <li data-id="element-208">Comunicarnos con usted respecto a su pedido.</li>
        <li data-id="element-209">Mejorar nuestros productos y servicios.</li>
        <li data-id="element-210">Cumplir con obligaciones legales y fiscales.</li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-211">3. Protección de Datos</h3>
      <p data-id="element-212">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger su información personal contra acceso no autorizado,
        alteración, divulgación o destrucción. Sin embargo, ningún método de
        transmisión por Internet es 100% seguro.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-213">
        4. Compartición de Datos
      </h3>
      <p data-id="element-214">
        <strong data-id="element-215">
          No vendemos, alquilamos ni compartimos su información personal con
          terceros
        </strong>
        , excepto en los siguientes casos:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-216">
        <li data-id="element-217">Proveedores de servicios de pago para procesar transacciones.</li>
        <li data-id="element-218">Cuando sea requerido por ley o autoridad competente.</li>
        <li data-id="element-219">Para proteger nuestros derechos legales.</li>
      </ul>

      <h3 className="font-bold text-dark text-base" data-id="element-220">5. Cookies</h3>
      <p data-id="element-221">
        Nuestro sitio puede utilizar cookies y tecnologías similares para
        mejorar la experiencia del usuario y recopilar datos analíticos. Usted
        puede configurar su navegador para rechazar cookies, aunque esto podría
        afectar la funcionalidad del sitio.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-222">6. Derechos del Usuario</h3>
      <p data-id="element-223">
        De acuerdo con la Ley Federal de Protección de Datos Personales en
        Posesión de los Particulares (LFPDPPP), usted tiene derecho a:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-224">
        <li data-id="element-225">
          <strong data-id="element-226">Acceso:</strong> conocer qué datos personales tenemos sobre
          usted.
        </li>
        <li data-id="element-227">
          <strong data-id="element-228">Rectificación:</strong> solicitar la corrección de sus datos.
        </li>
        <li data-id="element-229">
          <strong data-id="element-230">Cancelación:</strong> solicitar la eliminación de sus datos.
        </li>
        <li data-id="element-231">
          <strong data-id="element-232">Oposición:</strong> oponerse al tratamiento de sus datos para
          fines específicos.
        </li>
      </ul>
      <p data-id="element-233">
        Para ejercer estos derechos (derechos ARCO), contáctenos a través de los
        canales disponibles en este sitio.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-234">7. Retención de Datos</h3>
      <p data-id="element-235">
        Conservamos su información personal únicamente durante el tiempo
        necesario para cumplir con los fines descritos en esta política y con
        las obligaciones legales aplicables.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-236">8. Menores de Edad</h3>
      <p data-id="element-237">
        Nuestros productos no están dirigidos a menores de 18 años. No
        recopilamos intencionalmente información de menores de edad.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-238">
        9. Cambios en esta Política
      </h3>
      <p data-id="element-239">
        Nos reservamos el derecho de actualizar esta política en cualquier
        momento. Los cambios serán publicados en esta página con la fecha de
        última actualización.
      </p>

      <h3 className="font-bold text-dark text-base" data-id="element-240">10. Contacto</h3>
      <p data-id="element-241">
        Si tiene preguntas sobre esta Política de Privacidad o sobre el
        tratamiento de sus datos personales, puede contactarnos a través de los
        canales de comunicación disponibles en este sitio web.
      </p>
    </div>;
}